import { Helmet } from 'react-helmet';
import { useSEO } from '@/hooks/useSEO';

export interface ImageMetadata {
  url: string;
  alt: string;
  width?: number;
  height?: number;
  type?: string;
}

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string[];
  ogImage?: string;
  canonical?: string;
  images?: ImageMetadata[]; // Additional images for the page
}

const SEO = ({ title, description, keywords, ogImage, canonical, images }: SEOProps) => {
  const { seoData, loading } = useSEO();

  if (loading) return null;

  const siteUrl = 'https://capitaldistrictsocial.com';
  
  // Use props first, fallback to database settings, then defaults
  const metaTitle = title || seoData?.meta_title || "Capital District Social - American Bar & Restaurant in Tallahassee";
  const metaDescription = description || seoData?.meta_description || "Capital District Social is Tallahassee's home bar for FSU, FAMU & TCC. Enjoy craft cocktails, American fare, and an electric game day atmosphere!";
  const metaKeywords = keywords || seoData?.keywords || [
    'capital district social',
    'tallahassee bar',
    'tallahassee restaurant',
    'fsu bar',
    'famu bar',
    'tcc bar',
    'sports bar tallahassee',
    'american restaurant tallahassee',
    'craft cocktails tallahassee',
    'game day bar tallahassee'
  ];
  const ogImageUrl = ogImage || seoData?.og_image || `${siteUrl}/og-image.jpg`;
  const canonicalUrl = canonical || seoData?.canonical_url || `${siteUrl}${window.location.pathname}`;
  
  const robotsContent = seoData 
    ? `${seoData.robots_index ? 'index' : 'noindex'}, ${seoData.robots_follow ? 'follow' : 'nofollow'}`
    : 'index, follow';
  
  // Ensure image URLs are absolute
  const processedImages = images?.map(img => ({
    ...img,
    url: img.url.startsWith('http') ? img.url : `${siteUrl}${img.url}`
  })) || [];

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{metaTitle}</title>
      <meta name="title" content={metaTitle} />
      <meta name="description" content={metaDescription} />
      {metaKeywords.length > 0 && (
        <meta name="keywords" content={metaKeywords.join(', ')} />
      )}
      <meta name="robots" content={robotsContent} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={seoData?.og_title || metaTitle} />
      <meta property="og:description" content={seoData?.og_description || metaDescription} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:alt" content={metaTitle} />
      <meta property="og:site_name" content="Capital District Social" />
      <meta property="og:locale" content="en_US" />

      {/* Additional page images for Open Graph */}
      {processedImages.map((img, idx) => (
        <meta key={`og-img-${idx}`} property="og:image" content={img.url} />
      ))}
      {processedImages.map((img, idx) => img.alt && (
        <meta key={`og-img-alt-${idx}`} property="og:image:alt" content={img.alt} />
      ))}
      {processedImages.map((img, idx) => img.width && (
        <meta key={`og-img-width-${idx}`} property="og:image:width" content={img.width.toString()} />
      ))}
      {processedImages.map((img, idx) => img.height && (
        <meta key={`og-img-height-${idx}`} property="og:image:height" content={img.height.toString()} />
      ))}

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={canonicalUrl} />
      <meta property="twitter:title" content={seoData?.og_title || metaTitle} />
      <meta property="twitter:description" content={seoData?.og_description || metaDescription} />
      <meta property="twitter:image" content={ogImageUrl} />
      <meta property="twitter:image:alt" content={metaTitle} />

      {/* Additional SEO tags */}
      <meta name="author" content="Capital District Social" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      
      {/* Geo tags for local SEO */}
      <meta name="geo.region" content="US-FL" />
      <meta name="geo.placename" content="Tallahassee" />
      <meta name="geo.position" content="30.4383;-84.2807" />
      <meta name="ICBM" content="30.4383, -84.2807" />
    </Helmet>
  );
};

export default SEO;
